import { asImageSrc } from "@prismicio/client"
import React from "react"

import { DEFAULT_LANGUAGE } from "@bounce/i18n"
import type { Locale } from "@bounce/i18n"

import { PoiList } from "./components/PoiList/PoiList"
import { SearchBar } from "./components/SearchBar"
import { Title } from "./components/Title"
import { useFaqs } from "./hooks/useFaqs"
import { useListViewSearchBar } from "../../../components/LuggageStorage/StoreListSection/hooks/useListViewSearchBar"
import { FAQs } from "@/components/Generic/FAQs/FAQs"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import { MAX_CITY_LIST_VIEW_STORES } from "@/components/LuggageStorage/StoreListSection/constants"
import { StoreListSection } from "@/components/LuggageStorage/StoreListSection/StoreListSection"
import { useListViewBreadcrumbs } from "@/hooks/useListViewBreadcrumbs"
import type { CityData } from "@/libs/bounce/cities"
import { useCityUrl } from "@/libs/router"
import { useInterpolations } from "@/screens/LuggageStorage/CityScreen/hooks/useInterpolations"
import { useSEO } from "@/screens/LuggageStorage/CityScreen/hooks/useSEO"

export type CityListViewScreenProps = {
  alternateLanguages: Array<Locale["language"]>
  city: CityData
}

const Separator = () => <div className="pt-section" />

export const CityListViewScreen = ({ alternateLanguages, city }: CityListViewScreenProps) => {
  const interpolations = useInterpolations({ city })
  const { metaTitle, metaDescription } = useSEO({ city, interpolations, isPrismicMetaEnabled: false })
  const { absolutePath, getUrl } = useCityUrl({ city, directory: "luggage-storage" })
  const breadcrumbs = useListViewBreadcrumbs({ type: "city", city })
  const faqs = useFaqs({ city, interpolations })
  const initialSearchTerm = city.translatedName

  const { searchTerm, searchBarContent, setSearchTerm, setSearchBarContent } = useListViewSearchBar({
    from: "CityListView",
    initialValue: initialSearchTerm,
  })

  const alternates = alternateLanguages
    .map(language => ({ language, href: getUrl({ absolute: true, language }) }))
    .concat([{ language: "x-default", href: getUrl({ absolute: true, language: DEFAULT_LANGUAGE }) }])

  return (
    <Layout
      alternates={alternates}
      breadcrumbs={breadcrumbs}
      headProps={{
        title: metaTitle,
        description: metaDescription,
        url: absolutePath,
        image: asImageSrc(city.image, { ar: "1.91:1", fit: "crop" }),
      }}
      languages={alternateLanguages}
      from="City"
      searchQuery={city.query}
      hideNewsletterBanner
      hideDynamicAppBanner>
      <Title storesCount={MAX_CITY_LIST_VIEW_STORES} interpolations={interpolations} hasSearchTerm={!!searchTerm} />
      <SearchBar
        className="mt-6 max-w-screen-lg"
        initialValue={initialSearchTerm}
        value={searchBarContent}
        onSearch={setSearchTerm}
        onChange={setSearchBarContent}
      />
      {!searchTerm && <PoiList city={city} className="pt-6" />}

      <StoreListSection
        city={city}
        from="City"
        path={absolutePath}
        searchQuery={city.query}
        searchTerm={searchTerm}
        className="pt-8"
      />

      <Separator />
      <FAQs faqs={faqs} />
      <Separator />
    </Layout>
  )
}
